import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
    ShoppingBag,
    MapPin,
    Star,
    Award,
    Search,
    Filter,
    User,
    ShieldCheck,
    CheckCircle,
    X,
    Clock,
    Briefcase
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { providers, domesticWorkers, allSponsoredAds } from '../data/mockData';
import MockPayment from '../components/MockPayment';
import AdCarousel from '../components/AdCarousel';
import PriceCalculator from '../components/PriceCalculator';
import { supabase } from '../supabaseClient';

const MarketplaceView = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const initialCat = queryParams.get('cat') || 'All';

    const [activeCategory, setActiveCategory] = useState(initialCat);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [showPayment, setShowPayment] = useState(false);
    const [bookingAmount, setBookingAmount] = useState(0);
    const [passengers, setPassengers] = useState(1);
    const [suitcases, setSuitcases] = useState(1);
    const [showCalculator, setShowCalculator] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [dbProviders, setDbProviders] = useState(providers);
    
    const { addBooking, user } = useApp();

    useEffect(() => {
        const fetchProviders = async () => {
            const { data, error } = await supabase.from('providers').select('*');
            if (data && !error) {
                setDbProviders(data);
            }
        };
        fetchProviders();
    }, []);

    const categories = ['All', 'Drivers', 'Chefs', 'Logistics', 'Hairstylists/Barbers', 'Domestic Workers', 'Tutors'];

    const baseProviders = activeCategory === 'All'
        ? [...dbProviders, ...domesticWorkers.map(dw => ({ ...dw, category: 'Domestic Workers' }))]
        : (activeCategory === 'Domestic Workers'
            ? domesticWorkers.map(dw => ({ ...dw, category: 'Domestic Workers' }))
            : dbProviders.filter(p => p.category === activeCategory).filter(p => {
                if (activeCategory === 'Drivers' && p.passengers !== undefined) {
                    return p.passengers >= passengers && p.suitcases >= suitcases;
                }
                return true;
            }));

    const filteredProviders = baseProviders.filter(p => 
        (p.name && p.name.toLowerCase().includes(searchQuery.toLowerCase())) || 
        (p.category && p.category.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (p.type && p.type.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleBook = (p) => {
        setBookingAmount(p.rate || 10000);
        setShowPayment(true);
    };

    const relevantAds = activeCategory === 'All'
        ? allSponsoredAds.slice(0, 4)
        : allSponsoredAds.filter(ad => ad.category === activeCategory || ad.category.includes(activeCategory));

    // Add fallback if category has no specific ads
    const displayAds = relevantAds.length > 0 ? relevantAds : allSponsoredAds.slice(0, 3);

    return (
        <div className="marketplace-view animate-fade">
            {!selectedProvider ? (
                <>
                    <div className="market-header" style={{ padding: '20px 20px 0' }}>
                        <h2 style={{ fontSize: '28px', color: 'var(--text-dark)', fontWeight: '800' }}>Concierge Service</h2>
                        <p style={{ fontSize: '15px', color: 'var(--text-light)', marginTop: '4px' }}>Verified professionals for your stay in Lagos.</p>
                    </div>

                    <div className="search-row" style={{ padding: '20px', display: 'flex', gap: '12px' }}>
                        <div className="search-box-m" style={{ flex: 1, position: 'relative', background: 'white', borderRadius: '30px', border: '1px solid #eee', boxShadow: '0 8px 24px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', padding: '0 18px' }}>
                            <Search size={18} color="#999" />
                            <input 
                                type="text" 
                                placeholder="Search by name or skill..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                style={{ border: 'none', background: 'none', outline: 'none', padding: '14px 10px', width: '100%', fontSize: '15px' }} 
                            />
                        </div>
                        <button className="filter-btn" style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'white', border: '1px solid #eee', display: 'flex', alignItems: 'center', justify_content: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.05)' }}>
                            <Filter size={20} color="var(--primary-green)" />
                        </button>
                    </div>

                    <div className="cat-scroll">
                        {categories.map(cat => (
                            <button
                                key={cat}
                                className={`cat-pill ${activeCategory === cat ? 'active' : ''}`}
                                onClick={() => setActiveCategory(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>

                    {activeCategory === 'Logistics' && (
                        <div className="card animate-fade" style={{ margin: '0 20px 24px', background: 'linear-gradient(135deg, #4f46e5, #7c3aed)', color: 'white', padding: '20px', borderRadius: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                    <h4 style={{ color: 'white', fontSize: '16px', margin: 0 }}>Need a quick delivery?</h4>
                                    <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px', marginTop: '4px', marginBottom: 0 }}>Compare rates across providers</p>
                                </div>
                                <button 
                                    onClick={() => setShowCalculator(true)}
                                    style={{ background: 'white', color: '#4f46e5', border: 'none', padding: '10px 16px', borderRadius: '12px', fontWeight: 'bold', fontSize: '13px', cursor: 'pointer' }}
                                >
                                    Calculate
                                </button>
                            </div>
                        </div>
                    )}

                    <AdCarousel providers={displayAds} title={activeCategory === 'All' ? "Sponsored Services" : `Sponsored ${activeCategory}`} />

                    {activeCategory === 'Drivers' && (
                        <div className="driver-filters card animate-fade">
                            <h4>Vehicle Requirements (Airport Pickup)</h4>
                            <div className="filter-controls">
                                <div className="f-control">
                                    <label>Passengers</label>
                                    <div className="stepper">
                                        <button onClick={() => setPassengers(Math.max(1, passengers - 1))}>-</button>
                                        <span>{passengers}</span>
                                        <button onClick={() => setPassengers(passengers + 1)}>+</button>
                                    </div>
                                </div>
                                <div className="f-control">
                                    <label>Suitcases</label>
                                    <div className="stepper">
                                        <button onClick={() => setSuitcases(Math.max(0, suitcases - 1))}>-</button>
                                        <span>{suitcases}</span>
                                        <button onClick={() => setSuitcases(suitcases + 1)}>+</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="provider-grid" style={{ padding: '0 20px 40px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        {filteredProviders.map(p => (
                            <div key={p.id} className="p-card" onClick={() => setSelectedProvider(p)} style={{ background: 'white', borderRadius: '24px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column' }}>
                                <div className="p-img-box" style={{ width: '100%', aspectRatio: '1/1', position: 'relative' }}>
                                    <img src={p.photo} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                    {p.verified && (
                                        <div className="v-star-badge" style={{ position: 'absolute', top: '10px', right: '10px', background: 'white', padding: '4px', borderRadius: '50%', display: 'flex', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                            <Star size={12} fill="var(--gold-verified)" color="var(--gold-verified)" />
                                        </div>
                                    )}
                                    <div className="p-rating-pill" style={{ position: 'absolute', bottom: '10px', left: '10px', background: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '3px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                                        4.9 <Star size={10} fill="var(--gold-verified)" color="var(--gold-verified)" />
                                    </div>
                                </div>
                                <div className="p-details" style={{ padding: '12px 14px' }}>
                                    <h4 style={{ fontSize: '15px', fontWeight: '800', color: 'var(--text-dark)', marginBottom: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</h4>
                                    <span style={{ fontSize: '10px', color: 'var(--text-light)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{p.category}</span>
                                    <div style={{ marginTop: '8px', fontSize: '14px', fontWeight: '850', color: 'var(--text-dark)' }}>
                                        ₦{p.rate?.toLocaleString() || '---'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div className="provider-profile-detailed animate-fade">
                    <button className="back-link" onClick={() => setSelectedProvider(null)}>← Back to List</button>

                    <div className="profile-top">
                        <div className="profile-p-img">
                            <img src={selectedProvider.photo} alt={selectedProvider.name} />
                            {selectedProvider.verified && <div className="verified-badge-lg"><Award size={16} /> EZ Verified</div>}
                        </div>
                        <div className="profile-p-id">
                            <h2 style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                {selectedProvider.name}
                                {selectedProvider.verified && <Star size={20} fill="var(--gold-verified)" color="var(--gold-verified)" title="Verified Provider" />}
                            </h2>
                            <p className="p-tag">
                                {selectedProvider.category}
                                {selectedProvider.type && ` • ${selectedProvider.type}`}
                                {selectedProvider.vehicle && ` (${selectedProvider.vehicle})`}
                                • {selectedProvider.area || 'Lagos'}
                            </p>
                            <div className="p-stats">
                                <div className="stat"><Star size={14} fill="var(--gold-verified)" /> 4.9 (42 Reviews)</div>
                            </div>
                        </div>
                    </div>

                    <div className="profile-sections">
                        <div className="sec card">
                            <div className="sec-header">
                                <ShieldCheck size={18} color="var(--primary-green)" />
                                <h4>Verification Status</h4>
                            </div>
                            <div className="v-list">
                                <div className="v-item pulse"><CheckCircle size={14} /> ID Verified</div>
                                <div className="v-item pulse"><CheckCircle size={14} /> Profile Photo Verified</div>
                                <div className="v-item pulse"><CheckCircle size={14} /> Background Check - OK</div>
                            </div>
                        </div>

                        {selectedProvider.packages && (
                            <div className="packages-sec">
                                <h3>Pricing Packages</h3>
                                {selectedProvider.packages.map((pkg, i) => (
                                    <div key={i} className="pkg-card card">
                                        <div className="pkg-info">
                                            <h5>{pkg.name}</h5>
                                            <p>Standard service with guarantee.</p>
                                        </div>
                                        <span className="pkg-price">₦{pkg.price.toLocaleString()}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="reviews-sec">
                            <div className="rev-header">
                                <h3>Reviews</h3>
                                <span>See All</span>
                            </div>
                            <div className="review-item card">
                                <div className="rev-user">
                                    <div className="rev-avatar">AD</div>
                                    <div className="rev-meta">
                                        <b>Adebayo D.</b>
                                        <span>2 days ago</span>
                                    </div>
                                </div>
                                <div className="rev-scores">
                                    <div className="score"><span>Cost</span> <b>5.0</b></div>
                                    <div className="score"><span>Quality</span> <b>4.8</b></div>
                                    <div className="score"><span>Punctuality</span> <b>5.0</b></div>
                                </div>
                                <p className="rev-comment">Fantastic service! Highly recommended for anyone visiting Lagos for the first time.</p>
                            </div>
                        </div>
                    </div>

                    <div className="sticky-action">
                        <button className="primary-btn full-w lg-btn" onClick={() => handleBook(selectedProvider)}>
                            Book This Provider
                        </button>
                    </div>
                </div>
            )}

            <MockPayment
                isOpen={showPayment}
                onClose={() => setShowPayment(false)}
                amount={bookingAmount}
                title="Service Booking"
                onSuccess={() => {
                    addBooking({
                        id: Date.now(),
                        type: selectedProvider.category,
                        provider: selectedProvider.name,
                        time: "Scheduled for Today",
                        status: "Confirmed",
                        amount: bookingAmount
                    });
                    setSelectedProvider(null);
                }}
            />

            <PriceCalculator 
                isOpen={showCalculator} 
                onClose={() => setShowCalculator(false)} 
            />

            <style dangerouslySetInnerHTML={{
                __html: `
        .marketplace-view { padding-bottom: 80px; }
        .market-header { margin-bottom: 20px; }
        .market-header h2 { font-size: 24px; }
        .market-header p { font-size: 14px; color: var(--text-light); }

        .search-row { display: flex; gap: 12px; margin-bottom: 20px; }
        .search-box-m { flex: 1; background: var(--card-gray); border: 1px solid var(--border-gray); border-radius: 12px; padding: 0 16px; display: flex; align-items: center; gap: 12px; }
        .search-box-m input { border: none; background: none; outline: none; width: 100%; height: 48px; font-size: 15px; }
        .filter-btn { width: 48px; height: 48px; border-radius: 12px; background: var(--card-gray); border: 1px solid var(--border-gray); display: flex; align-items: center; justify-content: center; }

        .cat-scroll { display: flex; gap: 10px; overflow-x: auto; scrollbar-width: none; margin-bottom: 24px; padding: 0 20px 4px; }
        .cat-scroll::-webkit-scrollbar { display: none; }
        .cat-pill { padding: 10px 22px; border-radius: 30px; border: 1px solid var(--border-gray); background: white; white-space: nowrap; font-size: 14px; font-weight: 600; color: var(--text-light); transition: 0.2s; box-shadow: 0 4px 6px rgba(0,0,0,0.02); }
        .cat-pill.active { background: var(--primary-green); border-color: var(--primary-green); color: white; box-shadow: 0 8px 16px rgba(0, 104, 56, 0.2); }

        .provider-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .p-card { flex-direction: column; padding: 12px; cursor: pointer; transition: 0.2s; }
        .p-card:active { transform: scale(0.98); }
        .p-img-box { position: relative; width: 100%; aspect-ratio: 1/1; border-radius: 10px; overflow: hidden; margin-bottom: 12px; }
        .p-img-box img { width: 100%; height: 100%; object-fit: cover; }
        .verified-badge-mini { position: absolute; top: 8px; right: 8px; background: var(--gold-verified); color: white; width: 20px; height: 20px; border-radius: 50%; display: flex; align-items: center; justify-content: center; }

        .p-name-row { display: flex; flex-direction: column; margin-bottom: 8px; }
        .p-name-row h4 { font-size: 14px; margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .p-category { font-size: 10px; font-weight: 700; color: var(--primary-green); text-transform: uppercase; }
        .p-meta { display: flex; gap: 10px; margin-bottom: 12px; }
        .p-rating, .p-area { font-size: 11px; display: flex; align-items: center; gap: 3px; color: var(--text-light); font-weight: 600; }
        .p-footer { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
        .p-rate { font-weight: 800; font-size: 14px; color: var(--text-dark); }
        .sm-btn { padding: 6px 14px; font-size: 11px; }

        .back-link { background: none; color: var(--primary-green); font-weight: 700; margin-bottom: 20px; padding: 0; }
        .profile-top { display: flex; gap: 20px; margin-bottom: 30px; align-items: center; }
        .profile-p-img { position: relative; }
        .profile-p-img img { width: 90px; height: 90px; border-radius: 50%; object-fit: cover; border: 3px solid var(--primary-green); }
        .verified-badge-lg { position: absolute; bottom: -5px; left: 50%; transform: translateX(-50%); background: var(--gold-verified); color: white; padding: 2px 8px; border-radius: 10px; font-size: 9px; font-weight: 800; white-space: nowrap; display: flex; align-items: center; gap: 4px; }
        .profile-p-id h2 { font-size: 24px; margin: 0; }
        .p-tag { color: var(--text-light); font-size: 14px; font-weight: 500; }
        .p-stats { margin-top: 8px; }
        .stat { font-size: 13px; font-weight: 600; color: var(--text-dark); display: flex; align-items: center; gap: 4px; }

        .profile-sections { display: flex; flex-direction: column; gap: 24px; }
        .sec-header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
        .sec-header h4 { font-size: 16px; margin: 0; }
        .v-list { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .v-item { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700; color: var(--primary-green); }

        .packages-sec h3, .reviews-sec h3 { font-size: 18px; margin-bottom: 16px; }
        .pkg-card { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .pkg-info h5 { font-size: 15px; margin-bottom: 2px; }
        .pkg-info p { font-size: 12px; color: var(--text-light); margin: 0; }
        .pkg-price { font-weight: 800; color: var(--primary-green); }

        .rev-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
        .rev-header span { color: var(--primary-green); font-size: 13px; font-weight: 600; }
        .rev-user { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
        .rev-avatar { width: 34px; height: 34px; border-radius: 50%; background: var(--light-green); color: var(--primary-green); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 800; }
        .rev-meta { flex: 1; display: flex; flex-direction: column; }
        .rev-meta b { font-size: 14px; }
        .rev-meta span { font-size: 11px; color: var(--text-light); }
        .rev-scores { display: flex; gap: 16px; margin-bottom: 12px; border-bottom: 1px solid #f0f0f0; padding-bottom: 8px; }
        .score { font-size: 11px; display: flex; flex-direction: column; }
        .score span { color: var(--text-light); }
        .rev-comment { font-size: 13px; line-height: 1.5; color: var(--text-dark); margin: 0; }

        .sticky-action { position: fixed; bottom: 85px; left: 50%; transform: translateX(-50%); width: 100%; max-width: 480px; padding: 0 20px; z-index: 100; }
        .lg-btn { height: 56px; border-radius: 28px; font-size: 16px; box-shadow: 0 8px 24px rgba(0, 104, 56, 0.3); }

        .driver-filters { margin-bottom: 24px; border: 1px solid var(--border-gray); }
        .driver-filters h4 { font-size: 14px; margin-bottom: 12px; }
        .filter-controls { display: flex; gap: 20px; }
        .f-control { flex: 1; display: flex; flex-direction: column; gap: 8px; }
        .f-control label { font-size: 12px; color: var(--text-light); font-weight: 700; text-transform: uppercase; }
        .stepper { display: flex; align-items: center; justify-content: space-between; background: var(--card-gray); border-radius: 8px; padding: 4px; }
        .stepper button { width: 32px; height: 32px; border-radius: 6px; background: white; border: 1px solid var(--border-gray); font-size: 16px; font-weight: 800; color: var(--text-dark); display: flex; align-items: center; justify-content: center; cursor: pointer; }
        .stepper button:active { background: var(--light-green); border-color: var(--primary-green); color: var(--primary-green); }
        .stepper span { font-size: 14px; font-weight: 800; width: 30px; text-align: center; }
      `}} />
        </div>
    );
};

export default MarketplaceView;
